<template>
  <LayoutDefault>
    <ContainerLayoutWrapper>
      <ContainerError :error="error" :organization="organization" />
    </ContainerLayoutWrapper>
  </LayoutDefault>
</template>

<script lang="ts" setup>
import LayoutDefault from '@/layouts/LayoutDefault.vue'
import { DeepBoxError, isDeepBoxError } from '@/models'
import { isSentryEnabled } from '@/utils/helpers/env.ts'
import * as Sentry from '@sentry/vue'
import { useHead } from '@unhead/vue'
import ContainerError from '@/components/containers/ContainerError.vue'
import ContainerLayoutWrapper from '@/components/containers/ContainerLayoutWrapper.vue'
import type { UserMeOrganization } from '@deepcloud/api-deep-admin'

const props = defineProps({
  error: {
    type: Object as PropType<DeepBoxError | Error>,
    default: undefined,
  },
  organization: {
    type: Object as PropType<UserMeOrganization>,
    default: undefined,
  },
})

const { t, te } = useI18n()

const pageTitle = computed(() => {
  const err = props.error
  if (!err) return ''

  const message = err.message

  if (isDeepBoxError(err) && message) {
    return te(message) ? t(message) : message
  }

  if (!message) {
    const status = isDeepBoxError(err) ? err.statusCode : undefined
    return status === 404
      ? t('error.page_not_found')
      : t('error.error_occurred')
  }

  return message
})

useHead(() => ({
  title: pageTitle.value,
}))

function createSentryError(err: unknown): Error {
  if (err instanceof Error) return err

  if (err && typeof err === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ('message' in err) return new Error(String((err as any).message))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ('data' in err) return new Error(String((err as any).data))
  }

  return new Error(String(err))
}

watch(
  () => props.error,
  (err) => {
    if (!err || isDeepBoxError(err) || !isSentryEnabled()) return
    Sentry.captureException(createSentryError(err))
  },
  { immediate: true },
)
</script>
